<template>
	<div>
		<GlobalHeader />
		<LocationsPageHero />
		<LocationsPageSection type="airports" />
		<LazyVisibleLocationsPageSection type="departments" hydrate-on-visible />
		<LazyVisibleLocationsPageSection v-if="locale === 'pl'" type="other" hydrate-on-visible />
		<LazyVisibleLocationsPageSection type="delivery-buses" hydrate-on-visible />
		<LazyVisibleLocationsPageSection type="passenger-buses" hydrate-on-visible />
		<LazyVisibleGlobalDepartmentsFullMap :contact="true" hydrate-on-visible />
		<LazyVisibleGlobalNumberOneSection hydrate-on-visible />
		<LazyVisibleGlobalApplicationBanner hydrate-on-visible />
		<LazyVisibleGlobalFooter hydrate-on-visible />
	</div>
</template>

<script setup lang="ts">
import { airportsOrder, branchesOrder } from '@/data/branchesPageData'
const CMSLocationsStore = useCMSLocationsStore()
const CMSLocations = ref<ICMSLocationModel[] | undefined>(undefined)
const $t = useI18n().t
const { locale } = useI18n()
const config = useNuxtApp().$config
const route = useRoute()

// const sortLocations = () => {
// 	const orderMap = new Map(cmsLocationsCustomOrder.map((item) => [item.namePL, item.order]))

// 	CMSLocations.value = CMSLocations.value?.sort((a, b) => {
// 		const orderA = orderMap.has(a.namePL) ? orderMap.get(a.namePL) : Infinity
// 		const orderB = orderMap.has(b.namePL) ? orderMap.get(b.namePL) : Infinity
// 		return orderA - orderB
// 	})
// }

CMSLocations.value = await CMSLocationsStore.fetchCMSLocations()

watch(
	() => CMSLocationsStore.getCMSLocations,
	(newValue) => {
		if (newValue) {
			CMSLocations.value = newValue
			// sortLocations()
		}
	},
	{
		immediate: true,
		deep: true,
	},
)

const airportsData = computed(() =>
	CMSLocations.value
		?.filter((l) => l.showOnLocations && l.icon === 'airplane' && !l.otherLocation && !l.deliveryBus && !l.passengerBus && l.isActivePL)
		.sort((a, b) => {
			const indexA = airportsOrder.indexOf(a.placeId)
			const indexB = airportsOrder.indexOf(b.placeId)
			// Handle missing placeIds (not found in the order array) by placing them at the end
			return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB)
		}),
)

const branchesData = computed(() =>
	CMSLocations.value
		?.filter((l) => l.showOnLocations && l.icon !== 'airplane' && !l.otherLocation && !l.deliveryBus && !l.passengerBus && l.isActivePL)
		.sort((a, b) => {
			const indexA = branchesOrder.indexOf(a.placeId)
			const indexB = branchesOrder.indexOf(b.placeId)
			return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB)
		}),
)

const otherLocationsData = computed(() => {
	const nameKey = `name${locale.value.toUpperCase()}`
	return CMSLocations.value
		?.filter((l) => l.showOnLocations && l.otherLocation && l.isActivePL)
		?.sort((a, b) => {
			const nameA = a[nameKey]?.toLowerCase() || ''
			const nameB = b[nameKey]?.toLowerCase() || ''
			return nameA.localeCompare(nameB)
		})
})

const deliveryBusesData = computed(() => {
	const nameKey = `name${locale.value.toUpperCase()}`
	return CMSLocations.value
		?.filter((l) => l.showOnLocations && l.deliveryBus && l.isActivePL)
		?.sort((a, b) => {
			const nameA = a[nameKey]?.toLowerCase() || ''
			const nameB = b[nameKey]?.toLowerCase() || ''
			return nameA.localeCompare(nameB)
		})
})
const passengerBusesData = computed(() => {
	const nameKey = `name${locale.value.toUpperCase()}`
	return CMSLocations.value
		?.filter((l) => l.showOnLocations && l.passengerBus && l.isActivePL)
		?.sort((a, b) => {
			const nameA = a[nameKey]?.toLowerCase() || ''
			const nameB = b[nameKey]?.toLowerCase() || ''
			return nameA.localeCompare(nameB)
		})
})

provide('airportsData', airportsData)
provide('branchesData', branchesData)
provide('otherLocationsData', otherLocationsData)
provide('deliveryBusesData', deliveryBusesData)
provide('passengerBusesData', passengerBusesData)

const sendAnalyticsData = () => {
	const gtmService = useGTMService()
	if (!gtmService) return
	gtmService.pageViewed({ page_type: 'locations' })
}

if (process.client) sendAnalyticsData()

const robotsMeta = config.public.environment === 'production-to-change' ? 'index, follow' : 'noindex, nofollow'

useHead({
	title: $t('pages.locations.meta_title'),
	link: [
		{
			rel: 'canonical',
			href: config.public.baseURL + route.fullPath,
		},
	],
	meta: [
		{ name: 'robots', content: robotsMeta },
		{
			name: 'description',
			content: $t('pages.locations.meta_description'),
		},
		{ property: 'og:title', content: $t('pages.locations.meta_title') },
		{
			property: 'og:description',
			content: $t('pages.locations.meta_description'),
		},
	],
})
</script>
